import { captureClick, captureEvent } from "./utils/posthog"

const UI_ID = "subdomain-assignment__company-search-select"
const SEARCH_DEBOUNCE_MS = 500

const field = document.getElementById("company")
const listbox = document.getElementById("company-list")
const clearButton = document.querySelector(".searchable-select__clear")
const status = document.getElementById("company-status")
const companies = Array.from(listbox.children, (option) => ({
  option,
  name: option.textContent.toLowerCase(),
}))

let active = null
let searchTimeout

function options() {
  return Array.from(listbox.children)
}

function setActive(option) {
  active?.classList.remove("is-active")
  active = option
  if (option) {
    option.classList.add("is-active")
    field.setAttribute("aria-activedescendant", option.id)
    option.scrollIntoView({ block: "nearest" })
  } else {
    field.removeAttribute("aria-activedescendant")
  }
}

function openList() {
  if (options().length === 0) {
    closeList()
    return
  }
  listbox.style.visibility = "visible"
  field.setAttribute("aria-expanded", "true")
}

function closeList() {
  listbox.style.visibility = ""
  field.setAttribute("aria-expanded", "false")
  setActive(null)
}

// `machtfit.search/search`: every space-separated word of the query is a substring.
// Non-matches leave the DOM, because `tests/e2e/registration.spec.ts` picks the first `li`.
function filter(query) {
  const words = query.toLowerCase().split(" ")
  const matches = companies
    .filter(({ name }) => words.every((word) => name.includes(word)))
    .map(({ option }) => option)
  listbox.replaceChildren(...matches)
  return matches.length
}

function companyId(option) {
  const id = option.dataset.companyId
  return /^\d+$/.test(id) ? Number(id) : id
}

function choose(option) {
  const name = option.textContent.trim()
  for (const { option: other } of companies) {
    other.setAttribute("aria-selected", String(other === option))
  }
  field.value = name
  clearButton.hidden = false
  filter(name)
  closeList()
  captureClick("selected_employer", { company_name: name, company_id: companyId(option) }, UI_ID)
}

function retype(value) {
  listbox.querySelector('[aria-selected="true"]')?.setAttribute("aria-selected", "false")
  clearButton.hidden = !value
  setActive(null)
  status.textContent = String(filter(value))
  openList()
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(
    () => captureEvent("searched_employer", { search_term: value }, UI_ID),
    SEARCH_DEBOUNCE_MS,
  )
}

function step(offset) {
  const remaining = options()
  if (remaining.length === 0) {
    return
  }
  openList()
  const current = remaining.indexOf(active)
  const start = current === -1 ? (offset > 0 ? -1 : 0) : current
  setActive(remaining[(start + offset + remaining.length) % remaining.length])
}

// Keeps focus in the field, so a click on an option or on the clear button lands
// before the list closes.
for (const element of [listbox, clearButton]) {
  element.addEventListener("mousedown", (event) => event.preventDefault())
}

listbox.addEventListener("click", (event) => {
  const option = event.target.closest('[role="option"]')
  if (option) {
    choose(option)
  }
})

clearButton.addEventListener("click", () => {
  field.value = ""
  retype("")
  field.focus()
})

field.addEventListener("input", () => retype(field.value))
field.addEventListener("focus", openList)
field.addEventListener("blur", closeList)

const EXPANDED_ONLY = {
  Home: () => setActive(options()[0]),
  End: () => setActive(options().at(-1)),
  Enter: () => choose(active),
  Escape: closeList,
}

field.addEventListener("keydown", (event) => {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault()
    step(event.key === "ArrowDown" ? 1 : -1)
    return
  }
  const action = EXPANDED_ONLY[event.key]
  if (!action || field.getAttribute("aria-expanded") !== "true") {
    return
  }
  if (event.key === "Enter" && !active) {
    return
  }
  event.preventDefault()
  action()
})

const errors = document.getElementById("subdomain-assignment-errors")
if (errors) {
  captureEvent("error_in_subdomain_assignment_form", JSON.parse(errors.textContent))
}

if (document.activeElement === field) {
  field.value ? retype(field.value) : openList()
} else if (field.value) {
  filter(field.value)
}
